(() => {
    const init = root => {
        if (!root || root.dataset.initialized) return;
        root.dataset.initialized = 'true';

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const header = root.querySelector('header');
        const navigation = root.querySelector('.tech-nav');
        const resizeObserver = new ResizeObserver(() => {
            root.style.setProperty('--tech-header-height', `${header.offsetHeight}px`);
            root.style.setProperty('--tech-nav-height', `${navigation.offsetHeight}px`);
        });
        resizeObserver.observe(header);
        resizeObserver.observe(navigation);

        const animate = element => {
            if (reducedMotion.matches) return;
            element.animate([
                { opacity: .4, transform: 'translateY(8px)' },
                { opacity: 1, transform: 'translateY(0)' }
            ], { duration: 350, easing: 'ease-out' });
        };

        const select = (buttons, active) => {
            buttons.forEach(button => button.setAttribute('aria-pressed', String(button === active)));
        };

        const riseButtons = root.querySelectorAll('[data-rise]');
        const riseCopy = root.querySelector('[data-rise-copy]');
        const riseDough = root.querySelectorAll('[data-rise-dough]');
        const riseCallouts = root.querySelectorAll('[data-rise-callout]');
        const initialRiseCopy = riseCopy.textContent;
        let riseHeight = 10;
        let riseFrame;

        // Use the same crust geometry in both chambers; only the ceiling differs.
        const drawRise = height => {
            riseHeight = height;
            const top = 250 - height;
            const curve = 250 - height * .6;
            const dough = `M135 250 C135 ${curve} 137 ${top} 155 ${top} C177 ${top} 177 240 195 240 L375 240 C393 240 393 ${top} 415 ${top} C433 ${top} 435 ${curve} 435 250 Z`;

            riseDough.forEach(path => path.setAttribute('d', dough));
            riseCallouts.forEach(path => path.setAttribute('d', `M216 190L183 199L158 ${top + 8}`));
        };

        const setRise = after => {
            cancelAnimationFrame(riseFrame);
            const target = after ? 42 : 10;

            if (reducedMotion.matches) {
                drawRise(target);
                return;
            }

            const initialHeight = riseHeight;
            const started = performance.now();
            const update = now => {
                const progress = Math.min((now - started) / 350, 1);
                const eased = 1 - (1 - progress) ** 3;
                drawRise(initialHeight + (target - initialHeight) * eased);
                if (progress < 1) riseFrame = requestAnimationFrame(update);
            };

            riseFrame = requestAnimationFrame(update);
        };

        riseButtons.forEach(button => button.addEventListener('click', () => {
            const after = button.dataset.rise === 'after';
            select(riseButtons, button);
            setRise(after);
            riseCopy.textContent = after ?
                'As the crust rises, the lower chamber brings it into the upper heat region sooner. The taller Raptor 1 chamber leaves more clearance while the center remains close to the stone.' : initialRiseCopy;
            animate(riseCopy);
        }));

        const turnPanel = root.querySelector('[data-inview-panel]');
        const turnButton = root.querySelector('button[data-turn]');
        const turnNote = root.querySelector('[data-inview-note]');
        const initialTurnNote = turnNote.textContent;

        turnButton.addEventListener('click', () => {
            const turned = turnButton.getAttribute('aria-pressed') !== 'true';
            turnButton.setAttribute('aria-pressed', String(turned));
            turnPanel.dataset.turned = String(turned);
            turnPanel.style.setProperty('--tech-raptor-rotation', turned ? '90deg' : '0deg');
            turnPanel.style.setProperty('--tech-other-rotation', turned ? '180deg' : '0deg');
            turnButton.querySelector('[data-turn-label]').textContent = turned ? 'Reset comparison' : 'Turn the Pizza';
            turnButton.querySelector('[data-turn-detail]').textContent = turned ? 'Return to starting position' : 'R1 90° · L / U 180°';
            turnNote.textContent = turned ? turnButton.dataset.turnedNote : initialTurnNote;

            root.querySelectorAll('[data-turn-status]').forEach(status => {
                status.textContent = turned ? `Turned ${status.dataset.turnStatus}° — rear crust brought into view` : 'Starting position';
            });
        });

        const flowPanel = root.querySelector('[data-flow-panel]');
        const steps = root.querySelectorAll('[data-flow-step]');
        const play = root.querySelector('[data-flow-play]');
        const flowStages = ['intake', 'bend', 'ports', 'flame'];
        let stage = 0;
        let timer;
        let playing = false;
        let flowVisible = true;

        const showStage = index => {
            stage = index;
            select(steps, steps[index]);
            flowPanel.dataset.stage = flowStages[index];
            const description = root.querySelector('.tech-flow-description');
            description.querySelector('[data-flow-title]').textContent = steps[index].querySelector('.tech-step-title').textContent;
            description.querySelector('[data-flow-copy]').textContent = steps[index].querySelector('.tech-step-copy').textContent;
            root.querySelector('[data-flow-note]').textContent = steps[index].dataset.note;
            animate(description);
        };

        // Pause flow motion when it is off screen or the browser tab is hidden.
        const syncFlowPlayback = () => {
            clearInterval(timer);
            timer = undefined;
            const active = playing && flowVisible && !document.hidden && !reducedMotion.matches;
            flowPanel.classList.toggle('is-playing', active);
            play.textContent = reducedMotion.matches ? 'Next Flow Stage' : playing ? 'Pause Flow' : 'Play Flow';
            play.setAttribute('aria-pressed', String(playing && !reducedMotion.matches));
            if (active) timer = setInterval(() => showStage((stage + 1) % steps.length), 3500);
        };

        const stop = () => {
            playing = false;
            syncFlowPlayback();
        };

        steps.forEach((button, index) => button.addEventListener('click', () => {
            stop();
            showStage(index);
        }));

        play.addEventListener('click', () => {
            if (reducedMotion.matches) {
                showStage((stage + 1) % steps.length);
                return;
            }
            playing = !playing;
            syncFlowPlayback();
        });

        const flowObserver = new IntersectionObserver(entries => {
            flowVisible = entries[0].isIntersecting;
            syncFlowPlayback();
        }, { threshold: .05 });
        flowObserver.observe(flowPanel);
        reducedMotion.addEventListener('change', stop);
        syncFlowPlayback();

        const preheatPanel = root.querySelector('[data-preheat-panel]');
        const preheatButtons = root.querySelectorAll('[data-preheat-stage]');
        preheatButtons.forEach(button => button.addEventListener('click', () => {
            select(preheatButtons, button);
            preheatPanel.dataset.stage = button.dataset.preheatStage;
            root.querySelector('[data-preheat-title]').textContent = button.dataset.title;
            root.querySelector('[data-preheat-copy]').textContent = button.dataset.copy;
        }));

        const insulationButtons = root.querySelectorAll('[data-insulation]');
        const insulationLayers = root.querySelectorAll('[data-ts-layer]');
        insulationButtons.forEach(button => button.addEventListener('click', () => {
            const selected = button.dataset.insulation;
            select(insulationButtons, button);
            insulationLayers.forEach(layer => {
                layer.setAttribute('opacity', selected === 'all' || layer.dataset.tsLayer === selected ? '1' : '.25');
            });
            root.querySelector('[data-insulation-note]').textContent = button.dataset.note;
        }));

        const fuelButtons = root.querySelectorAll('[data-fuel-air]');
        const fuelDiagram = root.querySelector('.tech-fuel-diagram');
        fuelButtons.forEach(button => button.addEventListener('click', () => {
            select(fuelButtons, button);
            fuelDiagram.dataset.air = button.dataset.fuelAir;
            root.querySelector('[data-fuel-note]').textContent = button.dataset.note;
        }));

        const thermalButtons = root.querySelectorAll('[data-thermal]');
        thermalButtons.forEach(button => button.addEventListener('click', () => {
            select(thermalButtons, button);
            root.querySelector('.tech-thermoflex-diagram').dataset.state = button.dataset.thermal;
            const copy = root.querySelector('[data-thermal-copy]');
            copy.textContent = button.dataset.thermal === 'cold' ?
                'Cold reference: the parts remain connected at their resting dimensions.' :
                'Heated: the joints accommodate limited relative movement as the metal expands. The parts remain connected.';
            animate(copy);
        }));

        const diagramMedia = window.matchMedia('(max-width: 900px)');
        const widgets = [...root.querySelectorAll('[data-tech-widget]')];

        const fitDiagram = widget => {
            const button = widget.querySelector('[data-widget-zoom]');
            const viewport = widget.querySelector('.tech-widget-viewport');
            delete widget.dataset.enlarged;
            button.textContent = 'Enlarge diagram';
            button.setAttribute('aria-pressed', 'false');
            viewport.removeAttribute('tabindex');
            viewport.scrollTo(0, 0);
        };

        widgets.forEach(widget => {
            const button = widget.querySelector('[data-widget-zoom]');
            const viewport = widget.querySelector('.tech-widget-viewport');
            const diagram = viewport.querySelector('svg');
            const label = diagram.querySelector('title').textContent;
            const width = Math.max(720, Math.min(1400, diagram.viewBox.baseVal.width * 1.25));
            widget.style.setProperty('--tech-diagram-width', `${width}px`);
            viewport.setAttribute('role', 'region');
            viewport.setAttribute('aria-label', label);
            button.hidden = false;

            button.addEventListener('click', () => {
                if (!diagramMedia.matches || widget.dataset.enlarged) {
                    fitDiagram(widget);
                    return;
                }
                widget.dataset.enlarged = 'true';
                button.textContent = 'Fit diagram';
                button.setAttribute('aria-pressed', 'true');
                viewport.setAttribute('tabindex', '0');
                viewport.focus({ preventScroll: true });
            });

            viewport.addEventListener('keydown', event => {
                if (event.key !== 'Escape' || !widget.dataset.enlarged) return;
                fitDiagram(widget);
                button.focus({ preventScroll: true });
            });
        });

        const onDiagramResize = () => {
            if (!diagramMedia.matches) widgets.forEach(fitDiagram);
        };
        diagramMedia.addEventListener('change', onDiagramResize);

        // Preserve the label limits used by the original diagrams after fonts load.
        const fitDiagramLabels = () => {
            root.querySelectorAll('[data-tech-label-width]').forEach(label => {
                const width = Number(label.dataset.techLabelWidth);
                if (label.getComputedTextLength() > width) {
                    label.setAttribute('textLength', width);
                    label.setAttribute('lengthAdjust', 'spacingAndGlyphs');
                }
            });
        };
        document.fonts.ready.then(() => {
            if (root.isConnected) fitDiagramLabels();
        });

        // Mobile selectors share the same content used in the desktop comparisons.
        ['comparison', 'burner', 'airflow'].forEach(group => {
            const buttons = root.querySelectorAll(`button[data-${group}]`);
            buttons.forEach(button => button.addEventListener('click', () => {
                root.dataset[group] = button.dataset[group];
                select(buttons, button);
                fitDiagramLabels();
                root.querySelectorAll(`[data-${group}-panel="${button.dataset[group]}"]`).forEach(animate);
            }));
        });

        const rail = navigation.querySelector('.tech-wrap');
        const links = [...navigation.querySelectorAll('a')];
        const chapters = [...root.querySelectorAll('.tech-chapter')];
        let frame;
        let activeId;

        const updateNavigation = () => {
            frame = undefined;
            const offset = header.offsetHeight + navigation.offsetHeight + 1;
            const active = chapters.filter(chapter => chapter.getBoundingClientRect().top <= offset).pop() || chapters[0];
            if (active.id === activeId) return;
            activeId = active.id;

            links.forEach(link => {
                if (link.hash === `#${activeId}`) {
                    link.setAttribute('aria-current', 'location');
                    const bounds = link.getBoundingClientRect();
                    const viewport = rail.getBoundingClientRect();
                    if (bounds.left < viewport.left || bounds.right > viewport.right) {
                        rail.scrollBy({ left: bounds.left - viewport.left - 17, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
                    }
                } else {
                    link.removeAttribute('aria-current');
                }
            });
        };

        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(updateNavigation);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        updateNavigation();

        const onVisibilityChange = () => {
            syncFlowPlayback();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('shopify:section:unload', event => {
            if (!event.target.contains(root)) return;
            stop();
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            cancelAnimationFrame(frame);
            cancelAnimationFrame(riseFrame);
            resizeObserver.disconnect();
            flowObserver.disconnect();
            reducedMotion.removeEventListener('change', stop);
            diagramMedia.removeEventListener('change', onDiagramResize);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        });
    };

    init(document.querySelector('[data-us-technologies]'));
    document.addEventListener('shopify:section:load', event => {
        init(event.target.querySelector('[data-us-technologies]'));
    });
})();
